import { useState, useEffect } from "react";
import { Link } from 'react-router-dom';
import useTokenStore from "../../stores/tokens";
import { MdCancel } from "react-icons/md";
import moment from 'moment';
import 'moment/locale/ko';
import { compatApi } from '../../utils/Intercepter';
import { FaCheck } from "react-icons/fa";
import certificateDataList from '../../certificateDataList';
import GradeBadge from '../../components/GradeBadge';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import { toast } from "react-toastify";
import toastConfig from "../../const/toastConfig";
import { downloadFile, isImageFile, loadImageWithAuth, revokeImageUrl } from "../../utils/ImageUtil";
import { PURPLE, MINT, hexToRgba } from "../../utils/designUtils";
import { useDaumPostcodePopup } from 'react-daum-postcode';

moment.locale('ko');

const MyInfoDetails = () => {
    const { user } = useTokenStore();
    const currentOrgCode = user.currentOrganizationCode;

    const initialUserInfoData = {
        userEmail: '',
        userName: '',
        userNameEn: '',
        contact: '',
        emergencyContact: '',
        department: '',
        position: '',
        experience: '',
        organizationName: '',
        gender: '',
        address: '',
        addressDetail: '',
        joinDate: '',
        birthDate: '',
        eduGrade: '',
        techGrade: '',
        careerList: [],
        certificateList: [],
        projectList: [],
        educationList: [],
        itTrainingList: [],
        specialSkillList: [],
        preProjectList: [],
        documentList: []
    };

    const fetchData = async () => {
        try {
            const response = await compatApi.get(`/my-info/details?organizationCode=${currentOrgCode}`);
            const rawData = response.data.data;
            if (rawData) {
                const sortedData = { ...rawData };
                if (sortedData.certificateList) {
                    sortedData.certificateList.sort((a, b) => {
                        const nameA = (a.certificateName || "").replace(/\s+/g, "");
                        const nameB = (b.certificateName || "").replace(/\s+/g, "");
                        const isAChungbo = nameA.includes("정보처리기사");
                        const isBChungbo = nameB.includes("정보처리기사");
                        if (isAChungbo && !isBChungbo) return -1;
                        if (!isAChungbo && isBChungbo) return 1;
                        const dateA = new Date((a.acquisitionDate || "1900.01").replace(/\./g, '-'));
                        const dateB = new Date((b.acquisitionDate || "1900.01").replace(/\./g, '-'));
                        return dateB - dateA;
                    });
                }
                if (sortedData.careerList) {
                    sortedData.careerList.sort((a, b) => {
                        const dateA = a.startDate ? new Date(a.startDate.replace(/\./g, '-')) : new Date('0001-01-01');
                        const dateB = b.startDate ? new Date(b.startDate.replace(/\./g, '-')) : new Date('0001-01-01');
                        return dateB - dateA;
                    });
                }
                if (sortedData.projectList) {
                    sortedData.projectList.sort((a, b) => {
                        const dateA = a.startDate ? new Date(a.startDate.replace(/\./g, '-')) : new Date('0001-01-01');
                        const dateB = b.startDate ? new Date(b.startDate.replace(/\./g, '-')) : new Date('0001-01-01');
                        return dateB - dateA;
                    });
                }
                if (sortedData.preProjectList) {
                    sortedData.preProjectList.sort((a, b) => {
                        const dateA = a.startDate ? new Date(a.startDate.replace(/\./g, '-')) : new Date('0001-01-01');
                        const dateB = b.startDate ? new Date(b.startDate.replace(/\./g, '-')) : new Date('0001-01-01');
                        return dateB - dateA;
                    });
                }
                setPersonalInfo(sortedData);
                setBeforeInput(sortedData);
            }
        } catch (error) {
            console.log(error);
            alert('오류가 발생했습니다.');
        }
    };

    const IconChevron = ({ width = 11, height = 11, style = {} }) => (
        <svg width={width} height={height} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={style}>
            <path d="m9 18 6-6-6-6" />
        </svg>
    );

    useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [personalInfo, setPersonalInfo] = useState(initialUserInfoData);
    const [isContactEditing, setIsContactEditing] = useState(false);
    const [isEmergencyContactEditing, setIsEmergencyContactEditing] = useState(false);
    const [beforeInput, setBeforeInput] = useState({});
    const [contactMethod, setContactMethod] = useState('');
    const [isCertificateAdding, setIsCertificateAdding] = useState(false);
    const [isCareerAdding, setIsCareerAdding] = useState(false);
    const [isEducationAdding, setIsEducationAdding] = useState(false);
    const [isPreProjectAdding, setIsPreProjectAdding] = useState(false);

    const [newCertificate, setNewCertificate] = useState({ certificateName: '', acquisitionDate: null, includeInCareerProfile: true });
    const [newCareer, setNewCareer] = useState({ organizationName: '', startDate: null, endDate: null, position: '', task: '' });
    const [newEducation, setNewEducation] = useState({ schoolName: '', startDate: null, endDate: null, major: '' });
    const [newPreProject, setNewPreProject] = useState({ projectName: '', client: '', startDate: null, endDate: null, companyName: '', role: '', os: '', dbInfo: '', language: '', content: '' });

    const [isCertificateEditing, setIsCertificateEditing] = useState(false);
    const [targetCertificateEditing, setTargetCertificateEditing] = useState(-1);
    const [editCertificate, setEditCertificate] = useState({});

    const [isCareerEditing, setIsCareerEditing] = useState(false);
    const [targetCareerEditing, setTargetCareerEditing] = useState(-1);
    const [editCareer, setEditCareer] = useState({});

    const [isPreProjectEditing, setIsPreProjectEditing] = useState(false);
    const [targetPreProjectEditing, setTargetPreProjectEditing] = useState(-1);
    const [editPreProject, setEditPreProject] = useState({});
    const [showPreProjectTechModal, setShowPreProjectTechModal] = useState(false);
    const [preProjectTechMode, setPreProjectTechMode] = useState('new');
    const [preProjectTechForm, setPreProjectTechForm] = useState({ os: '', dbInfo: '', language: '' });

    const [isEducationEditing, setIsEducationEditing] = useState(false);
    const [targetEducationEditing, setTargetEducationEditing] = useState(-1);
    const [editEducation, setEditEducation] = useState({});

    const [isItTrainingAdding, setIsItTrainingAdding] = useState(false);
    const [newItTraining, setNewItTraining] = useState({ title: '', institution: '', startDate: null, endDate: null, description: '' });
    const [isItTrainingEditing, setIsItTrainingEditing] = useState(false);
    const [targetItTrainingEditing, setTargetItTrainingEditing] = useState(-1);
    const [editItTraining, setEditItTraining] = useState({});

    const [isSpecialSkillAdding, setIsSpecialSkillAdding] = useState(false);
    const [newSpecialSkill, setNewSpecialSkill] = useState({ skillName: '', proficiency: '' });
    const [isSpecialSkillEditing, setIsSpecialSkillEditing] = useState(false);
    const [targetSpecialSkillEditing, setTargetSpecialSkillEditing] = useState(-1);
    const [editSpecialSkill, setEditSpecialSkill] = useState({});

    const [profilePreviewUrl, setProfilePreviewUrl] = useState(null);

    // eslint-disable-next-line no-unused-vars
    const [isJoinDateEditing, setIsJoinDateEditing] = useState(false);
    const [isGenderEditing, setIsGenderEditing] = useState(false);
    const [isBirthDateEditing, setIsBirthDateEditing] = useState(false);
    const [isAddressEditing, setIsAddressEditing] = useState(false);
    const [isUserNameEnEditing, setIsUserNameEnEditing] = useState(false);

    // 주소 검색 — 다른 화면(조직 생성/조직 정보/프로젝트)과 동일하게 다음 우편번호 서비스 사용
    const popupOpen = useDaumPostcodePopup();
    const handleAddressSearch = () => { popupOpen({ onComplete: handleAddressSearchComplete }); };
    const handleAddressSearchComplete = (data) => {
        let fullAddress = data.address;
        let extraAddress = '';
        if (data.addressType === 'R') {
            if (data.bname !== '') extraAddress += data.bname;
            if (data.buildingName !== '') extraAddress += (extraAddress !== '' ? `, ${data.buildingName}` : data.buildingName);
            if (extraAddress !== '') fullAddress += ` (${extraAddress})`;
        }
        // 주소가 바뀌면 이전 상세 주소는 의미가 없으므로 비운다
        setPersonalInfo(prev => ({ ...prev, address: fullAddress, addressDetail: '' }));
    };

    const genderOptions = [
        { value: '남', label: '남' },
        { value: '여', label: '여' }
    ];

    const degreeOptions = [
        { value: null, label: "없음" },
        { value: "전문학사", label: "전문학사" },
        { value: "학사", label: "학사" },
        { value: "석사", label: "석사" },
        { value: "박사", label: "박사" }
    ];

    const handleContactEditing = (method, url) => {
        if (url === 'contact') { setIsContactEditing(true); } else { setIsEmergencyContactEditing(true); }
        setContactMethod(method === 'post' ? 'post' : 'put');
    };

    const cancelContactEditing = (url) => {
        if (url === 'contact') { setIsContactEditing(false); } else { setIsEmergencyContactEditing(false); }
        setContactMethod('');
        setPersonalInfo(beforeInput);
    };

    const updateContactAndEmergencyContact = async (contact, url) => {
        try {
            const response = contactMethod === 'post'
                ? await compatApi.post('/my-info/' + url, contact)
                : await compatApi.put('/my-info/' + url, contact);
            const data = response.data;
            if (!data.result) { alert(data.message); setPersonalInfo(beforeInput); }
        } catch (error) {
            console.log(error);
            alert('오류가 발생했습니다.');
            setPersonalInfo(beforeInput);
        }
        setIsContactEditing(false);
        setIsEmergencyContactEditing(false);
    };

    const handleCreateCertificate = async () => {
        try {
            const response = await compatApi.post('/my-info/certificate', newCertificate);
            const data = response.data;
            if (data.result) {
                toast.success("자격증 정보가 등록되었습니다.", toastConfig);
                if (newCertificate.file && newCertificate.certificateName !== '운전면허증') {
                    await handleFileUpload('certificate', data.data.id, newCertificate.file);
                } else if (newCertificate.file && newCertificate.certificateName === '운전면허증') {
                    toast.info("운전면허증은 증빙서류를 업로드하지 않습니다.", toastConfig);
                }
                fetchData();
            } else { alert(data.message); }
        } catch (error) {
            console.log(error);
            alert('오류가 발생했습니다.');
            setPersonalInfo(beforeInput);
        }
        setNewCertificate({ certificateName: '', acquisitionDate: null, includeInCareerProfile: true });
        setIsCertificateAdding(false);
    };

    const cancelCreateCertificate = () => {
        setNewCertificate({ certificateName: '', acquisitionDate: null, includeInCareerProfile: true });
        setIsCertificateAdding(false);
    };

    const deleteCertificate = async (name, id) => {
        if (window.confirm(name + "자격증을 정말 삭제하시겠습니까?")) {
            try {
                const response = await compatApi.del('/my-info/certificate/' + id);
                const data = response.data;
                if (!data.result) { alert(data.message); setPersonalInfo(beforeInput); }
                fetchData();
            } catch (error) {
                console.log(error);
                alert('오류가 발생했습니다.');
                setPersonalInfo(beforeInput);
            }
        }
    };

    const modifyCertificate = (targetId, target) => {
        if (targetCertificateEditing !== -1 && isCertificateEditing) { alert('먼저 진행 중인 자격증을 먼저 수정하여 주십시오.'); return; }
        setIsCertificateEditing(true);
        setTargetCertificateEditing(targetId);
        setEditCertificate(target);
    };

    const cancelModifyCertificate = () => {
        setIsCertificateEditing(false);
        setTargetCertificateEditing(-1);
        setEditCertificate({});
    };

    const handleToggleCareerProfile = async (certificate) => {
        try {
            const updated = { ...certificate, includeInCareerProfile: !(certificate.includeInCareerProfile !== false) };
            const response = await compatApi.put('/my-info/certificate', updated);
            const data = response.data;
            if (!data.result) alert(data.message);
            fetchData();
        } catch (error) {
            console.log(error);
            alert('오류가 발생했습니다.');
        }
    };

    const handleUpdateCertificate = async () => {
        try {
            const response = await compatApi.put('/my-info/certificate', editCertificate);
            const data = response.data;
            if (!data.result) { alert(data.message); setPersonalInfo(beforeInput); }
            fetchData();
        } catch (error) {
            console.log(error);
            alert('오류가 발생했습니다.');
            setPersonalInfo(beforeInput);
        }
        setEditCertificate({});
        setIsCertificateEditing(false);
        setTargetCertificateEditing(-1);
    };

    const handleCreateCareer = async () => {
        if (personalInfo.joinDate && newCareer.startDate) {
            const careerEnd = newCareer.endDate ? new Date(newCareer.endDate) : new Date();
            if (new Date(newCareer.startDate) <= new Date() && careerEnd >= new Date(personalInfo.joinDate)) {
                toast.warning('입력한 경력 기간이 현재 재직 기간과 겹칩니다. 경력 합산 시 중복 기간은 자동으로 제거됩니다.', toastConfig);
            }
        }
        try {
            const response = await compatApi.post('/my-info/career', newCareer);
            const data = response.data;
            if (!data.result) { alert(data.message); setPersonalInfo(beforeInput); }
            fetchData();
        } catch (error) {
            console.log(error);
            alert('오류가 발생했습니다.');
            setPersonalInfo(beforeInput);
        }
        setNewCareer({ organizationName: '', startDate: null, endDate: null, position: '', task: '' });
        setIsCareerAdding(false);
    };

    const cancelCreateCareer = () => {
        setNewCareer({ organizationName: '', startDate: null, endDate: null, position: '', task: '' });
        setIsCareerAdding(false);
    };

    const deleteCareer = async (name, careerId) => {
        if (window.confirm(name + " 경력을 정말 삭제하시겠습니까?")) {
            try {
                const response = await compatApi.del('/my-info/career/' + careerId);
                const data = response.data;
                if (!data.result) { alert(data.message); setPersonalInfo(beforeInput); }
                fetchData();
            } catch (error) {
                console.log(error);
                alert('오류가 발생했습니다.');
                setPersonalInfo(beforeInput);
            }
        }
    };

    const modifyCareer = (targetId, target) => {
        if (targetCareerEditing !== -1 && isCareerEditing) { alert('먼저 진행 중인 경력을 먼저 수정하여 주십시오.'); return; }
        setIsCareerEditing(true);
        setTargetCareerEditing(targetId);
        setEditCareer(target);
    };

    const cancelModifyCareer = () => {
        setIsCareerEditing(false);
        setTargetCareerEditing(-1);
        setEditCareer({});
    };

    const handleUpdateCareer = async () => {
        if (personalInfo.joinDate && editCareer.startDate) {
            const careerEnd = editCareer.endDate ? new Date(editCareer.endDate) : new Date();
            if (new Date(editCareer.startDate) <= new Date() && careerEnd >= new Date(personalInfo.joinDate)) {
                toast.warning('입력한 경력 기간이 현재 재직 기간과 겹칩니다. 경력 합산 시 중복 기간은 자동으로 제거됩니다.', toastConfig);
            }
        }
        try {
            const response = await compatApi.put('/my-info/career', editCareer);
            const data = response.data;
            if (!data.result) { alert(data.message); setPersonalInfo(beforeInput); }
            fetchData();
        } catch (error) {
            console.log(error);
            alert('오류가 발생했습니다.');
            setPersonalInfo(beforeInput);
        }
        setIsCareerEditing(false);
        setTargetCareerEditing(-1);
        setEditCareer({});
    };

    const handleCreatePreProject = async () => {
        try {
            const response = await compatApi.post('/my-info/pre-project', newPreProject);
            const data = response.data;
            if (!data.result) { alert(data.message); setPersonalInfo(beforeInput); }
            fetchData();
        } catch (error) {
            console.log(error);
            alert('오류가 발생했습니다.');
            setPersonalInfo(beforeInput);
        }
        setNewPreProject({ projectName: '', client: '', startDate: null, endDate: null, companyName: '', role: '', os: '', dbInfo: '', language: '', content: '' });
        setIsPreProjectAdding(false);
    };

    const cancelCreatePreProject = () => {
        setNewPreProject({ projectName: '', client: '', startDate: null, endDate: null, companyName: '', role: '', os: '', dbInfo: '', language: '', content: '' });
        setIsPreProjectAdding(false);
    };

    const deletePreProject = async (name, id) => {
        if (window.confirm(name + " 프로젝트를 정말 삭제하시겠습니까?")) {
            try {
                const response = await compatApi.del('/my-info/pre-project/' + id);
                const data = response.data;
                if (!data.result) { alert(data.message); setPersonalInfo(beforeInput); }
                fetchData();
            } catch (error) {
                console.log(error);
                alert('오류가 발생했습니다.');
                setPersonalInfo(beforeInput);
            }
        }
    };

    const modifyPreProject = (targetId, target) => {
        if (targetPreProjectEditing !== -1 && isPreProjectEditing) { alert('먼저 진행 중인 경력을 먼저 수정하여 주십시오.'); return; }
        setIsPreProjectEditing(true);
        setTargetPreProjectEditing(targetId);
        setEditPreProject(target);
    };

    const cancelModifyPreProject = () => {
        setIsPreProjectEditing(false);
        setTargetPreProjectEditing(-1);
        setEditPreProject({});
    };

    const handleUpdatePreProject = async () => {
        try {
            const response = await compatApi.put(`/my-info/pre-project/${editPreProject.id}`, editPreProject);
            const data = response.data;
            if (!data.result) { alert(data.message); setPersonalInfo(beforeInput); }
            fetchData();
            toast.success(`프로젝트 ${editPreProject.projectName} 정보가 변경되었습니다.`);
        } catch (error) {
            console.log(error);
            alert('오류가 발생했습니다.');
            setPersonalInfo(beforeInput);
        }
        setIsPreProjectEditing(false);
        setTargetPreProjectEditing(-1);
        setEditPreProject({});
    };

    const handleCreateSpecialSkill = async () => {
        if (!newSpecialSkill.skillName.trim()) { toast.error('특수기술명을 입력하세요.', toastConfig); return; }
        try {
            const response = await compatApi.post('/my-info/special-skill', newSpecialSkill);
            const data = response.data;
            if (data.result) { toast.success('특수기술이 등록되었습니다.', toastConfig); fetchData(); }
            else { alert(data.message); }
        } catch (error) { alert('오류가 발생했습니다.'); }
        setNewSpecialSkill({ skillName: '', proficiency: '' });
        setIsSpecialSkillAdding(false);
    };

    const cancelCreateSpecialSkill = () => {
        setNewSpecialSkill({ skillName: '', proficiency: '' });
        setIsSpecialSkillAdding(false);
    };

    const deleteSpecialSkill = async (name, id) => {
        if (window.confirm(`"${name}" 특수기술을 정말 삭제하시겠습니까?`)) {
            try {
                const response = await compatApi.del('/my-info/special-skill/' + id);
                const data = response.data;
                if (!data.result) { alert(data.message); return; }
                toast.success('특수기술이 삭제되었습니다.', toastConfig);
                fetchData();
            } catch (error) { alert('오류가 발생했습니다.'); }
        }
    };

    const modifySpecialSkill = (targetId, target) => {
        if (targetSpecialSkillEditing !== -1 && isSpecialSkillEditing) { alert('먼저 진행 중인 특수기술을 수정하여 주십시오.'); return; }
        setIsSpecialSkillEditing(true);
        setTargetSpecialSkillEditing(targetId);
        setEditSpecialSkill(target);
    };

    const cancelModifySpecialSkill = () => {
        setIsSpecialSkillEditing(false);
        setTargetSpecialSkillEditing(-1);
        setEditSpecialSkill({});
    };

    const handleUpdateSpecialSkill = async () => {
        if (!editSpecialSkill.skillName?.trim()) { toast.error('특수기술명을 입력하세요.', toastConfig); return; }
        try {
            const response = await compatApi.put('/my-info/special-skill', editSpecialSkill);
            const data = response.data;
            if (!data.result) { alert(data.message); return; }
            toast.success('특수기술이 수정되었습니다.', toastConfig);
            fetchData();
        } catch (error) { alert('오류가 발생했습니다.'); }
        cancelModifySpecialSkill();
    };

    const handleOpenPreProjectTech = (mode) => {
        const source = mode === 'new' ? newPreProject : editPreProject;
        setPreProjectTechMode(mode);
        setPreProjectTechForm({ os: source.os || '', dbInfo: source.dbInfo || '', language: source.language || '' });
        setShowPreProjectTechModal(true);
    };

    const handleSavePreProjectTech = () => {
        if (preProjectTechMode === 'new') { setNewPreProject(prev => ({ ...prev, ...preProjectTechForm })); }
        else { setEditPreProject(prev => ({ ...prev, ...preProjectTechForm })); }
        setShowPreProjectTechModal(false);
    };

    const handleCreateEducation = async () => {
        try {
            const response = await compatApi.post('/my-info/education', newEducation);
            const data = response.data;
            if (data.result) {
                toast.success("학력 정보가 등록되었습니다.", toastConfig);
                if (newEducation.file) { await handleFileUpload('education', data.data.id, newEducation.file); }
                fetchData();
            } else { alert(data.message); }
        } catch (error) {
            console.log(error);
            alert('오류가 발생했습니다.');
            setPersonalInfo(beforeInput);
        }
        setNewEducation({ schoolName: '', startDate: null, endDate: null, major: '' });
        setIsEducationAdding(false);
    };

    const cancelCreateEducation = () => {
        setNewEducation({ schoolName: '', startDate: null, endDate: null, major: '' });
        setIsEducationAdding(false);
    };

    const deleteEducation = async (name, id) => {
        if (window.confirm(name + " 학력을 정말 삭제하시겠습니까?")) {
            try {
                const response = await compatApi.del('/my-info/education/' + id);
                const data = response.data;
                if (!data.result) { alert(data.message); setPersonalInfo(beforeInput); }
                fetchData();
            } catch (error) {
                console.log(error);
                alert('오류가 발생했습니다.');
                setPersonalInfo(beforeInput);
            }
        }
    };

    const modifyEducation = (targetId, target) => {
        if (targetEducationEditing !== -1 && isEducationEditing) { alert('먼저 진행 중인 학력을 먼저 수정하여 주십시오.'); return; }
        setIsEducationEditing(true);
        setTargetEducationEditing(targetId);
        setEditEducation(target);
    };

    const cancelModifyEducation = () => {
        setIsEducationEditing(false);
        setTargetEducationEditing(-1);
        setEditEducation({});
    };

    const handleUpdateEducation = async () => {
        try {
            const response = await compatApi.put('/my-info/education', editEducation);
            const data = response.data;
            if (!data.result) { alert(data.message); setPersonalInfo(beforeInput); }
            fetchData();
        } catch (error) {
            console.log(error);
            alert('오류가 발생했습니다.');
            setPersonalInfo(beforeInput);
        }
        cancelModifyEducation();
    };

    const handleCreateItTraining = async () => {
        if (!newItTraining.title.trim()) { toast.error('교육명을 입력하세요.', toastConfig); return; }
        if (!newItTraining.institution.trim()) { toast.error('교육기관을 입력하세요.', toastConfig); return; }
        if (!newItTraining.startDate) { toast.error('시작일을 입력하세요.', toastConfig); return; }
        try {
            const response = await compatApi.post('/my-info/it-training', newItTraining);
            const data = response.data;
            if (data.result) { toast.success('IT 교육 정보가 등록되었습니다.', toastConfig); fetchData(); }
            else { alert(data.message); }
        } catch (error) {
            console.log(error);
            alert('오류가 발생했습니다.');
        }
        setNewItTraining({ title: '', institution: '', startDate: null, endDate: null, description: '' });
        setIsItTrainingAdding(false);
    };

    const cancelCreateItTraining = () => {
        setNewItTraining({ title: '', institution: '', startDate: null, endDate: null, description: '' });
        setIsItTrainingAdding(false);
    };

    const deleteItTraining = async (name, id) => {
        if (window.confirm(`"${name}" 교육 정보를 정말 삭제하시겠습니까?`)) {
            try {
                const response = await compatApi.del('/my-info/it-training/' + id);
                const data = response.data;
                if (!data.result) { alert(data.message); return; }
                fetchData();
            } catch (error) {
                console.log(error);
                alert('오류가 발생했습니다.');
            }
        }
    };

    const modifyItTraining = (targetId, target) => {
        if (targetItTrainingEditing !== -1 && isItTrainingEditing) { alert('먼저 진행 중인 교육 정보를 수정하여 주십시오.'); return; }
        setIsItTrainingEditing(true);
        setTargetItTrainingEditing(targetId);
        setEditItTraining(target);
    };

    const cancelModifyItTraining = () => {
        setIsItTrainingEditing(false);
        setTargetItTrainingEditing(-1);
        setEditItTraining({});
    };

    const handleUpdateItTraining = async () => {
        if (!editItTraining.title?.trim()) { toast.error('교육명을 입력하세요.', toastConfig); return; }
        if (!editItTraining.institution?.trim()) { toast.error('교육기관을 입력하세요.', toastConfig); return; }
        if (!editItTraining.startDate) { toast.error('시작일을 입력하세요.', toastConfig); return; }
        try {
            const response = await compatApi.put('/my-info/it-training', editItTraining);
            const data = response.data;
            if (!data.result) { alert(data.message); return; }
            toast.success('IT 교육 정보가 수정되었습니다.', toastConfig);
            fetchData();
        } catch (error) {
            console.log(error);
            alert('오류가 발생했습니다.');
        }
        cancelModifyItTraining();
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (isImageFile(file.name)) {
            const localUrl = URL.createObjectURL(file);
            if (profilePreviewUrl) revokeImageUrl(profilePreviewUrl);
            setProfilePreviewUrl(localUrl);
        }
        const formData = new FormData();
        formData.append("file", file);
        formData.append("organizationCode", currentOrgCode);
        try {
            const response = await compatApi.upload('/my-info/profile-image', formData);
            if (response.data.result) {
                toast.success("프로필 사진이 업데이트되었습니다.", toastConfig);
                loadProfilePreview(personalInfo.userEmail);
                fetchData();
            } else {
                toast.error(response.data.message, toastConfig);
                loadProfilePreview(personalInfo.userEmail);
            }
        } catch (error) {
            console.error("이미지 업로드 실패:", error);
            toast.error("업로드 중 오류가 발생했습니다.", toastConfig);
            loadProfilePreview(personalInfo.userEmail);
        }
    };

    const handleImageDownload = async () => {
        if (!personalInfo.imageUrl) { alert("다운로드할 사진이 없습니다."); return; }
        const fileName = `${personalInfo.userName}_profile.png`;
        const success = await downloadFile(personalInfo.userEmail, fileName, '/my-info/download/profile');
        if (success) { toast.success('프로필 사진 다운로드가 완료되었습니다.', toastConfig); }
        else { toast.error('파일 다운로드에 실패했습니다.', toastConfig); }
    };

    const loadProfilePreview = async (userEmail) => {
        if (!userEmail) return;
        try {
            const url = await loadImageWithAuth(userEmail, '/my-info/download/profile');
            if (url) {
                if (profilePreviewUrl) revokeImageUrl(profilePreviewUrl);
                setProfilePreviewUrl(url);
            }
        } catch (error) { console.error("프로필 미리보기 로드 실패:", error); }
    };

    useEffect(() => {
        if (personalInfo.userEmail) { loadProfilePreview(personalInfo.userEmail); }
        return () => { if (profilePreviewUrl) revokeImageUrl(profilePreviewUrl); };
    }, [personalInfo.userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFileUpload = async (type, id, file) => {
        if (!id || id === 'undefined') { toast.error("항목 저장(체크 버튼)을 먼저 완료한 후 파일을 업로드해주세요.", toastConfig); return; }
        if (!file) return;
        if (type === 'certificate') {
            const targetCert = personalInfo.certificateList.find(c => c.id === id);
            const certName = targetCert ? targetCert.certificateName : newCertificate.certificateName;
            if (certName === '운전면허증') { toast.warning("운전면허증은 파일 업로드가 제한됩니다.", toastConfig); return; }
        }
        const formData = new FormData();
        formData.append("file", file);
        formData.append("id", id);
        formData.append("organizationCode", currentOrgCode);
        try {
            const response = await compatApi.upload(`/my-info/${type}/file`, formData);
            if (response.data.result) { toast.success("파일이 성공적으로 업로드되었습니다.", toastConfig); await fetchData(); }
            else { toast.error(response.data.message, toastConfig); }
        } catch (error) {
            console.error(`${type} 파일 업로드 실패:`, error);
            toast.error("업로드 중 오류가 발생했습니다.", toastConfig);
        }
    };

    const handleFileDownload = async (type, id, originalFileName) => {
        if (!originalFileName) { alert("다운로드할 파일이 없습니다."); return; }
        const success = await downloadFile(id, originalFileName, `/my-info/download/${type}`);
        if (success) { toast.success('파일 다운로드가 완료되었습니다.', toastConfig); }
        else { toast.error('파일 다운로드에 실패했습니다.', toastConfig); }
    };

    const handleDocumentUpload = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        const formData = new FormData();
        Array.from(files).forEach(file => { formData.append("files", file); });
        formData.append("organizationCode", currentOrgCode);
        try {
            const response = await compatApi.upload('/my-info/document/upload', formData);
            if (response.data.result) { toast.success(`${files.length}개의 문서가 업로드되었습니다.`, toastConfig); fetchData(); }
            else { toast.error(response.data.message, toastConfig); }
        } catch (error) {
            console.error("문서 업로드 실패:", error);
            toast.error("업로드 중 오류가 발생했습니다.", toastConfig);
        }
    };

    const handleDeleteDocument = async (id, fileName) => {
        if (window.confirm(`[${fileName}] 문서를 삭제하시겠습니까?`)) {
            try {
                const response = await compatApi.del(`/my-info/document/${id}`);
                if (response.data.result) { toast.success("문서가 삭제되었습니다.", toastConfig); fetchData(); }
            } catch (error) { toast.error("삭제 중 오류가 발생했습니다."); }
        }
    };

    const updateBasicInfo = async (value, urlField) => {
        try {
            const response = await compatApi.put('/my-info/' + urlField, value);
            const data = response.data;
            if (!data.result) { alert(data.message); setPersonalInfo(beforeInput); }
            else { toast.success("정보가 업데이트되었습니다.", toastConfig); fetchData(); }
        } catch (error) {
            console.error(error);
            alert('오류가 발생했습니다.');
            setPersonalInfo(beforeInput);
        }
        setIsGenderEditing(false);
        setIsBirthDateEditing(false);
        setIsAddressEditing(false);
        setIsUserNameEnEditing(false);
    };

    const updateAddress = async () => {
        if (!personalInfo.address && personalInfo.addressDetail?.trim()) {
            toast.warn('주소 검색으로 주소를 먼저 입력해주세요.', toastConfig);
            return;
        }
        try {
            const response = await compatApi.put('/my-info/address', {
                address: personalInfo.address || '',
                addressDetail: (personalInfo.addressDetail || '').trim(),
            });
            const data = response.data;
            if (!data.result) { alert(data.message); setPersonalInfo(beforeInput); }
            else { toast.success("정보가 업데이트되었습니다.", toastConfig); fetchData(); }
        } catch (error) {
            console.error(error);
            alert('오류가 발생했습니다.');
            setPersonalInfo(beforeInput);
        }
        setIsAddressEditing(false);
    };

    const handleProfileDownload = async () => {
        try {
            const requestData = {
                ...personalInfo,
                certificateList: (personalInfo.certificateList || []).filter(c => c.includeInCareerProfile !== false),
                itTrainingList: personalInfo.itTrainingList || [],
            };
            const response = await compatApi.postFile('/my-info/download/profile-doc', requestData, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const fileName = `경력프로필_${personalInfo.userName || '미기입'}.docx`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("경력프로필 다운로드가 완료되었습니다.", toastConfig);
        } catch (error) {
            console.error("경력프로필 생성 실패:", error);
            toast.error("경력프로필 생성 중 오류가 발생했습니다.", toastConfig);
        }
    };

    const calcMergedDays = (ranges) => {
        const valid = ranges
            .filter(r => r.start)
            .map(r => ({ s: moment(r.start).startOf('day').valueOf(), e: moment(r.end || undefined).startOf('day').valueOf() }))
            .filter(r => r.e >= r.s)
            .sort((a, b) => a.s - b.s);
        if (!valid.length) return 0;
        const merged = [{ ...valid[0] }];
        for (let i = 1; i < valid.length; i++) {
            const last = merged[merged.length - 1];
            if (valid[i].s <= last.e) last.e = Math.max(last.e, valid[i].e);
            else merged.push({ ...valid[i] });
        }
        return merged.reduce((sum, r) => sum + Math.round((r.e - r.s) / 86400000) + 1, 0);
    };

    const calculateFullCareer = () => {
        const careerList = personalInfo.careerList || [];
        const isJoinDateMissing = !personalInfo.joinDate;
        const allRanges = [
            ...careerList.map(c => ({ start: c.startDate, end: c.endDate })),
            ...(isJoinDateMissing ? [] : [{ start: personalInfo.joinDate, end: null }])
        ];
        const totalDays = calcMergedDays(allRanges);
        const prevOnlyDays = calcMergedDays(careerList.map(c => ({ start: c.startDate, end: c.endDate })));
        const currentOrgDays = isJoinDateMissing ? 0 : calcMergedDays([{ start: personalInfo.joinDate, end: null }]);
        const convertDaysToText = (days) => {
            if (days <= 0) return "0개월";
            const years = Math.floor(days / 365);
            const months = Math.floor((days % 365) / (365 / 12));
            const yearStr = years > 0 ? `${years}년 ` : "";
            const monthStr = months > 0 ? `${months}개월` : (years === 0 ? "0개월" : "");
            const totalMonthCount = (years * 12) + months;
            const totalMonthStr = totalMonthCount > 0 ? `(총 ${totalMonthCount}개월)` : "";
            return `${yearStr}${monthStr} ${totalMonthStr}`.trim();
        };
        const totalCareerText = convertDaysToText(totalDays);
        const prevCareerText = prevOnlyDays > 0 ? convertDaysToText(prevOnlyDays) : null;
        const currentCareerText = isJoinDateMissing ? "입사일 없음" : convertDaysToText(currentOrgDays);
        return (
            <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                <span style={{ background: `linear-gradient(135deg,${hexToRgba(PURPLE, .16)},${hexToRgba(MINT, .16)})`, color: PURPLE, fontWeight: 700, fontSize: 13, padding: '5px 12px', borderRadius: 8 }}>총 경력 {totalCareerText}</span>
                {prevCareerText && <span style={{ fontSize: 12, color: '#7a7989' }}>이전 경력 {prevCareerText}</span>}
                {prevCareerText && <span style={{ color: '#d4d3df' }}>·</span>}
                {isJoinDateMissing
                    ? <strong style={{ color: '#ef4444', fontSize: 12 }}>{currentCareerText}</strong>
                    : <span style={{ fontSize: 12, color: '#7a7989' }}>현재 {currentCareerText}</span>}
            </span>
        );
    };

    // ── style helpers ──────────────────────────────────────────────────────
    const cardS = {
        background: 'white', borderRadius: 16,
        border: '1px solid #f0eff5',
        boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
        overflow: 'hidden',
    };

    const b = (v, sm) => {
        const h = sm ? 28 : 34; const p = sm ? '0 8px' : '0 14px';
        const base = { height: h, padding: p, borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: sm ? 12 : 13, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, whiteSpace: 'nowrap' };
        if (v === 'primary') return { ...base, background: PURPLE, color: 'white' };
        if (v === 'danger') return { ...base, background: '#ef4444', color: 'white' };
        if (v === 'outline-primary') return { ...base, background: 'white', color: PURPLE, border: `1px solid ${PURPLE}` };
        if (v === 'outline-secondary') return { ...base, background: 'white', color: '#6b7280', border: '1px solid #d1d5db' };
        if (v === 'outline-success') return { ...base, background: 'white', color: '#16a34a', border: '1px solid #86efac' };
        if (v === 'success') return { ...base, background: '#22c55e', color: 'white' };
        return base;
    };

    const inp = {
        height: 34, borderRadius: 6, border: '1.5px solid #ececf2',
        background: '#fafaff', padding: '0 10px', fontSize: 13,
        color: '#1f1d2e', outline: 'none', fontFamily: 'inherit',
        boxShadow: 'none', width: '100%', maxWidth: 'none', margin: 0,
    };

    const selS = {
        control: (base, state) => ({ ...base, borderColor: state.isFocused ? PURPLE : '#ececf2', boxShadow: 'none', '&:hover': { borderColor: PURPLE }, minHeight: 34, fontSize: 13 }),
        option: (base, state) => ({ ...base, background: state.isSelected ? PURPLE : state.isFocused ? '#f0eeff' : 'white', color: state.isSelected ? 'white' : '#1f1d2e' }),
        singleValue: (base) => ({ ...base, color: '#1f1d2e' }),
    };

    const tblS = { width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 13 };
    const tdS = { padding: '14px 14px', borderBottom: '1px solid #f4f3f9', fontSize: 12.5, color: '#3a3849', verticalAlign: 'middle', textAlign: 'left' };
    const edTdS = { padding: '8px 12px', borderBottom: '1px solid #f4f3f9', verticalAlign: 'middle' };
    const thS = { padding: '11px 14px', fontSize: 11.5, fontWeight: 600, color: '#5d5b6e', borderBottom: '1px solid #ececf2', whiteSpace: 'nowrap', letterSpacing: '.02em', background: '#f7f6fb', textAlign: 'center' };

    const sHead = (title, count, action, badgeColor) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: 'linear-gradient(180deg,#faf9fd 0%,#f5f4fa 100%)', borderBottom: '1px solid #ececf2' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 4, height: 16, borderRadius: 2, background: `linear-gradient(180deg,${PURPLE},${MINT})`, flexShrink: 0, display: 'inline-block' }} />
                <span style={{ fontSize: 14.5, fontWeight: 600, color: '#1f1d2e' }}>{title}</span>
                {count != null && <span style={{ fontSize: 11, fontWeight: 600, background: hexToRgba(badgeColor || PURPLE, .12), color: badgeColor || PURPLE, padding: '2px 8px', borderRadius: 999, lineHeight: 1.45 }}>{count}</span>}
            </div>
            {action}
        </div>
    );

    const addBtn = (label = '추가', onClick) => (
        <button onClick={onClick} style={{ height: 30, padding: '0 12px', borderRadius: 8, background: 'white', border: `1.5px solid ${hexToRgba(PURPLE, .4)}`, color: PURPLE, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 14, lineHeight: 1, marginTop: -1 }}>+</span> {label}
        </button>
    );

    const rowAct = (onEdit, onDelete) => (
        <div style={{ display: 'inline-flex', gap: 4 }}>
            <button title="수정" onClick={onEdit} style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid #ececf2', background: 'white', color: '#5d5b6e', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
            </button>
            <button title="삭제" onClick={onDelete} style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid #ececf2', background: 'white', color: '#5d5b6e', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
            </button>
        </div>
    );

    const gradeBadge = (grade) => {
        const gs = { '특급': { bg: hexToRgba(PURPLE, .14), color: PURPLE }, '고급': { bg: hexToRgba(MINT, .14), color: MINT }, '중급': { bg: '#fff4dc', color: '#c89427' }, '초급': { bg: '#ececf2', color: '#5d5b6e' } }[grade] || { bg: '#ececf2', color: '#5d5b6e' };
        return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: gs.bg, color: gs.color, fontWeight: 600, fontSize: 11.5, whiteSpace: 'nowrap' }}>{grade || '-'}</span>;
    };

    const emptyRow = (colSpan, msg) => (
        <tr><td colSpan={colSpan}>
            <div style={{ padding: '44px 20px', textAlign: 'center', color: '#aaa9b7', fontSize: 13 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: hexToRgba(PURPLE, .08), color: PURPLE, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                </div>
                <div>{msg}</div>
            </div>
        </td></tr>
    );

    return (
        <>
            <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)' }}>
                <div style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px' }}>

                    {/* 페이지 헤더 */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24 }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 12, color: '#8b8a99' }}>
                                <span>조직</span>
                                <IconChevron style={{ transform: "rotate(0deg)", opacity: 0.6 }} />
                                <span style={{ color: '#3a3849', fontWeight: 500 }}>내 정보 관리</span>
                            </div>
                            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em' }}>내 정보 관리</h1>
                            <p style={{ margin: '4px 0 0', fontSize: 13.5, color: '#7a7989' }}>개인 정보, 경력, 자격, 프로젝트 이력을 한 곳에서 관리하세요.</p>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Link to="/my-info/change-pw" style={{ height: 38, padding: '0 16px', borderRadius: 10, background: 'white', border: '1.5px solid #ececf2', color: '#3a3849', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
                                </svg>
                                비밀번호 변경
                            </Link>
                            <button onClick={handleProfileDownload} style={{ height: 38, padding: '0 18px', borderRadius: 10, background: `linear-gradient(135deg,${PURPLE},${MINT})`, border: 'none', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', boxShadow: `0 6px 14px -6px ${hexToRgba(PURPLE, .55)}`, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                경력프로필 다운로드
                            </button>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                        {/* 기본 정보 */}
                        <div style={cardS}>
                            {sHead('기본 정보', null, null)}
                            <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 28, padding: '24px 24px 12px' }}>
                                {/* 프로필 이미지 */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <div style={{ width: 160, height: 160, borderRadius: 18, background: `linear-gradient(135deg,${hexToRgba(PURPLE, .14)},${hexToRgba(MINT, .14)})`, border: `1.5px dashed ${hexToRgba(PURPLE, .3)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14, overflow: 'hidden' }}>
                                        {profilePreviewUrl
                                            ? <img src={profilePreviewUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            : <div style={{ width: 70, height: 70, borderRadius: '50%', background: `linear-gradient(135deg,${PURPLE},${MINT})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 22, boxShadow: `0 10px 24px -8px ${hexToRgba(PURPLE, .5)}` }}>{personalInfo.userName?.charAt(0) || '?'}</div>}
                                    </div>
                                    <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                                        <label htmlFor="profile-upload" style={{ height: 30, padding: '0 12px', borderRadius: 7, background: 'white', border: `1.5px solid ${hexToRgba(PURPLE, .4)}`, color: PURPLE, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center' }}>사진 업로드</label>
                                        <input id="profile-upload" type="file" accept="image/*" hidden onChange={handleImageUpload} />
                                        <button style={{ height: 30, padding: '0 12px', borderRadius: 7, background: 'white', border: '1.5px solid #ececf2', color: '#5d5b6e', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }} onClick={handleImageDownload}>다운로드</button>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: '#1f1d2e', marginBottom: 2 }}>{personalInfo.userName}</div>
                                        <div style={{ fontSize: 11.5, color: '#8b8a99' }}>{personalInfo.organizationName}</div>
                                    </div>
                                </div>

                                {/* 필드 목록 */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 32 }}>
                                    {/* 성명 */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                        <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>성명</div>
                                        <div style={{ fontSize: 13.5, color: '#1f1d2e', fontWeight: 500 }}>{personalInfo.userName}</div>
                                    </div>
                                    {/* 영어 이름 - 성명 옆에 배치 */}
                                    {isUserNameEnEditing ? (
                                        <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                            <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>영어 이름</div>
                                            <input type="text" style={inp} maxLength={50} value={personalInfo.userNameEn || ''} placeholder="예: Gildong Hong" onChange={(e) => setPersonalInfo({ ...personalInfo, userNameEn: e.target.value })} />
                                            <div style={{ display: 'flex', gap: 4 }}>
                                                <button style={b('primary', true)} onClick={() => updateBasicInfo(JSON.stringify((personalInfo.userNameEn || '').trim()), 'user-name-en')}><FaCheck /></button>
                                                <button style={b('danger', true)} onClick={() => { setIsUserNameEnEditing(false); setPersonalInfo(beforeInput); }}><MdCancel /></button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                            <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>영어 이름</div>
                                            <div style={{ fontSize: 13.5, color: '#1f1d2e', fontWeight: 500 }}>{personalInfo.userNameEn || '-'}</div>
                                            <button title="수정" onClick={() => setIsUserNameEnEditing(true)} style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid #ececf2', background: 'white', color: '#8b8a99', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 140ms' }} onMouseEnter={(e) => { e.currentTarget.style.color = PURPLE; e.currentTarget.style.borderColor = PURPLE; }} onMouseLeave={(e) => { e.currentTarget.style.color = '#8b8a99'; e.currentTarget.style.borderColor = '#ececf2'; }}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                            </button>
                                        </div>
                                    )}
                                    {/* 이메일 - full width (성명/영어 이름 한 줄 배치로 인해 단독 행) */}
                                    <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '108px 1fr', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                        <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>이메일</div>
                                        <div style={{ fontSize: 13.5, color: '#1f1d2e', fontWeight: 500 }}>{personalInfo.userEmail || '-'}</div>
                                    </div>
                                    {/* 연락처 */}
                                    {isContactEditing ? (
                                        <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                            <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>연락처</div>
                                            <input type="text" style={inp} value={personalInfo.contact || ''} placeholder="xxx-xxx-xxxx" onChange={(e) => setPersonalInfo({ ...personalInfo, contact: e.target.value })} />
                                            <div style={{ display: 'flex', gap: 4 }}>
                                                <button style={b('primary', true)} onClick={() => updateContactAndEmergencyContact(personalInfo.contact, 'contact')}><FaCheck /></button>
                                                <button style={b('danger', true)} onClick={() => cancelContactEditing('contact')}><MdCancel /></button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                            <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>연락처</div>
                                            <div style={{ fontSize: 13.5, color: '#1f1d2e', fontWeight: 500 }}>{personalInfo.contact || '-'}</div>
                                            <button title="수정" onClick={() => handleContactEditing(personalInfo.contact == null ? 'post' : 'put', 'contact')} style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid #ececf2', background: 'white', color: '#8b8a99', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 140ms' }} onMouseEnter={(e) => { e.currentTarget.style.color = PURPLE; e.currentTarget.style.borderColor = PURPLE; }} onMouseLeave={(e) => { e.currentTarget.style.color = '#8b8a99'; e.currentTarget.style.borderColor = '#ececf2'; }}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                            </button>
                                        </div>
                                    )}
                                    {/* 긴급연락처 */}
                                    {isEmergencyContactEditing ? (
                                        <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                            <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>긴급연락처</div>
                                            <input type="text" style={inp} value={personalInfo.emergencyContact || ''} placeholder="xxx-xxx-xxxx" onChange={(e) => setPersonalInfo({ ...personalInfo, emergencyContact: e.target.value })} />
                                            <div style={{ display: 'flex', gap: 4 }}>
                                                <button style={b('primary', true)} onClick={() => updateContactAndEmergencyContact(personalInfo.emergencyContact, 'emergency-contact')}><FaCheck /></button>
                                                <button style={b('danger', true)} onClick={() => cancelContactEditing('emergency-contact')}><MdCancel /></button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                            <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>긴급연락처</div>
                                            <div style={{ fontSize: 13.5, color: '#1f1d2e', fontWeight: 500 }}>{personalInfo.emergencyContact || '-'}</div>
                                            <button title="수정" onClick={() => handleContactEditing(personalInfo.emergencyContact == null ? 'post' : 'put', 'emergency-contact')} style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid #ececf2', background: 'white', color: '#8b8a99', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 140ms' }} onMouseEnter={(e) => { e.currentTarget.style.color = PURPLE; e.currentTarget.style.borderColor = PURPLE; }} onMouseLeave={(e) => { e.currentTarget.style.color = '#8b8a99'; e.currentTarget.style.borderColor = '#ececf2'; }}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                            </button>
                                        </div>
                                    )}
                                    {/* 생년월일 */}
                                    {isBirthDateEditing ? (
                                        <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                            <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>생년월일</div>
                                            <input type="date" style={inp} value={personalInfo.birthDate || ''} onChange={(e) => setPersonalInfo({ ...personalInfo, birthDate: e.target.value })} />
                                            <div style={{ display: 'flex', gap: 4 }}>
                                                <button style={b('primary', true)} onClick={() => updateBasicInfo(personalInfo.birthDate, 'birth-date')}><FaCheck /></button>
                                                <button style={b('danger', true)} onClick={() => { setIsBirthDateEditing(false); setPersonalInfo(beforeInput); }}><MdCancel /></button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                            <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>생년월일</div>
                                            <div style={{ fontSize: 13.5, color: '#1f1d2e', fontWeight: 500 }}>{personalInfo.birthDate || '-'}</div>
                                            <button title="수정" onClick={() => setIsBirthDateEditing(true)} style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid #ececf2', background: 'white', color: '#8b8a99', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 140ms' }} onMouseEnter={(e) => { e.currentTarget.style.color = PURPLE; e.currentTarget.style.borderColor = PURPLE; }} onMouseLeave={(e) => { e.currentTarget.style.color = '#8b8a99'; e.currentTarget.style.borderColor = '#ececf2'; }}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                            </button>
                                        </div>
                                    )}
                                    {/* 성별 */}
                                    {isGenderEditing ? (
                                        <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                            <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>성별</div>
                                            <Select isSearchable={false} options={genderOptions} styles={{ ...selS, container: (base) => ({ ...base, marginTop: 2 }) }} defaultValue={genderOptions.find(opt => opt.value === personalInfo.gender)} onChange={(e) => setPersonalInfo({ ...personalInfo, gender: e.value })} />
                                            <div style={{ display: 'flex', gap: 4 }}>
                                                <button style={b('primary', true)} onClick={() => updateBasicInfo(personalInfo.gender, 'gender')}><FaCheck /></button>
                                                <button style={b('danger', true)} onClick={() => { setIsGenderEditing(false); setPersonalInfo(beforeInput); }}><MdCancel /></button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                            <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>성별</div>
                                            <div style={{ fontSize: 13.5, color: '#1f1d2e', fontWeight: 500 }}>{personalInfo.gender || '-'}</div>
                                            <button title="수정" onClick={() => setIsGenderEditing(true)} style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid #ececf2', background: 'white', color: '#8b8a99', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 140ms' }} onMouseEnter={(e) => { e.currentTarget.style.color = PURPLE; e.currentTarget.style.borderColor = PURPLE; }} onMouseLeave={(e) => { e.currentTarget.style.color = '#8b8a99'; e.currentTarget.style.borderColor = '#ececf2'; }}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                            </button>
                                        </div>
                                    )}
                                    {/* 주소 - full width */}
                                    <div style={{ gridColumn: '1 / -1' }}>
                                        {isAddressEditing ? (
                                            <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'start', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                                <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500, lineHeight: '34px' }}>주소</div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                        {/* 주소는 검색 결과로만 채운다(직접 입력 불가) */}
                                                        <input type="text" readOnly style={{ ...inp, flex: 1, background: '#f5f4fa', cursor: 'pointer' }} value={personalInfo.address || ''} placeholder="주소 검색을 눌러 주소를 선택하세요" onClick={handleAddressSearch} />
                                                        <button style={b('outline-primary')} onClick={handleAddressSearch}>주소 검색</button>
                                                    </div>
                                                    <input type="text" style={inp} maxLength={100} value={personalInfo.addressDetail || ''} placeholder="상세 주소를 입력하세요 (동/호수 등)" disabled={!personalInfo.address} onChange={(e) => setPersonalInfo({ ...personalInfo, addressDetail: e.target.value })} />
                                                </div>
                                                <div style={{ display: 'flex', gap: 4, marginTop: 3 }}>
                                                    <button style={b('primary', true)} onClick={updateAddress}><FaCheck /></button>
                                                    <button style={b('danger', true)} onClick={() => { setIsAddressEditing(false); setPersonalInfo(beforeInput); }}><MdCancel /></button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                                <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>주소</div>
                                                <div style={{ fontSize: 13.5, color: '#1f1d2e', fontWeight: 500 }}>{personalInfo.address ? `${personalInfo.address}${personalInfo.addressDetail ? ' ' + personalInfo.addressDetail : ''}` : '-'}</div>
                                                <button title="수정" onClick={() => setIsAddressEditing(true)} style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid #ececf2', background: 'white', color: '#8b8a99', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 140ms' }} onMouseEnter={(e) => { e.currentTarget.style.color = PURPLE; e.currentTarget.style.borderColor = PURPLE; }} onMouseLeave={(e) => { e.currentTarget.style.color = '#8b8a99'; e.currentTarget.style.borderColor = '#ececf2'; }}>
                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {/* 입사일 - full width */}
                                    <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '108px 1fr', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                        <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>입사일</div>
                                        <div style={{ fontSize: 13.5, color: '#1f1d2e', fontWeight: 500 }}>{personalInfo.joinDate || '-'}</div>
                                    </div>
                                    {/* 등급 - 학력 기준 / 기술 기준 (서버가 계산·저장한 값을 그대로 표시, 한 줄에 함께 표시) */}
                                    {/* 상위 2단 그리드(columnGap: 32)와 동일한 구조를 그대로 복제해야 "기술등급" 라벨이 이메일/성별과 같은 위치에서 시작한다 */}
                                    <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 32, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr', alignItems: 'center', gap: 12 }}>
                                            <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>학력등급</div>
                                            <div><GradeBadge grade={personalInfo.eduGrade} /></div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr', alignItems: 'center', gap: 12 }}>
                                            <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>기술등급</div>
                                            <div><GradeBadge grade={personalInfo.techGrade} /></div>
                                        </div>
                                    </div>
                                    {/* 경력 - full width */}
                                    <div style={{ gridColumn: '1 / -1', padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr', gap: 12, alignItems: 'start' }}>
                                            <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500, paddingTop: 2 }}>경력</div>
                                            <div>{calculateFullCareer()}</div>
                                        </div>
                                    </div>
                                    {/* 나의 문서 - full width */}
                                    <div style={{ gridColumn: '1 / -1', padding: '12px 0' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr', gap: 12, alignItems: 'center' }}>
                                            <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>나의 문서</div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                                                {personalInfo.documentList && personalInfo.documentList.map((doc, index) => (
                                                    <div key={index} style={{ display: 'flex', alignItems: 'center', background: hexToRgba(PURPLE, .08), borderRadius: 6, padding: '4px 10px', gap: 6 }}>
                                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: PURPLE, flexShrink: 0 }}><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
                                                        <span style={{ cursor: 'pointer', fontSize: 12, fontWeight: 500, color: PURPLE, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} onClick={() => handleFileDownload('document', doc.id, doc.fileName)} title={doc.fileName}>{doc.fileName}</span>
                                                        <button onClick={() => handleDeleteDocument(doc.id, doc.fileName)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa9b7', padding: 0, display: 'flex', alignItems: 'center', fontSize: 15, lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#aaa9b7'}>×</button>
                                                    </div>
                                                ))}
                                                <label htmlFor="doc-upload-integrated" style={{ height: 32, padding: '0 14px', borderRadius: 8, background: '#2a2841', border: 'none', color: 'white', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                                    + 문서 추가
                                                </label>
                                                <input id="doc-upload-integrated" type="file" multiple hidden onChange={handleDocumentUpload} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 학력 */}
                        <div style={cardS}>
                            {sHead('학력', personalInfo.educationList.length, addBtn('추가', () => setIsEducationAdding(true)))}
                            <div style={{ overflowX: 'auto' }}>
                                <table style={tblS}>
                                    <colgroup>
                                        <col width={'18%'} /><col width={'10%'} /><col width={'*'} /><col width={'20%'} /><col width={'17%'} /><col width={'6%'} />
                                    </colgroup>
                                    <thead>
                                        <tr style={{ background: '#f7f6fb' }}>
                                            <th style={{ ...thS, textAlign: 'left' }}>학교명</th>
                                            <th style={thS}>학위</th>
                                            <th style={thS}>기간</th>
                                            <th style={{ ...thS, textAlign: 'left' }}>전공</th>
                                            <th style={thS}>증명서</th>
                                            <th style={thS}>관리</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isEducationAdding && (
                                            <tr>
                                                <td style={edTdS}><input type="text" style={inp} value={newEducation.schoolName || ''} onChange={(e) => setNewEducation({ ...newEducation, schoolName: e.target.value })} /></td>
                                                <td style={edTdS}><Select isSearchable={false} styles={selS} defaultValue={degreeOptions[0]} options={degreeOptions} onChange={(e) => setNewEducation({ ...newEducation, degree: e.value })} /></td>
                                                <td style={{ ...edTdS, textAlign: 'center' }}>
                                                    <input type="date" style={{ ...inp, display: 'inline-block', width: '45%' }} value={newEducation.startDate || ''} onChange={(e) => { if (newEducation.endDate && e.target.value > newEducation.endDate) { toast.error('시작일은 종료일보다 늦을 수 없습니다.', toastConfig); return; } setNewEducation({ ...newEducation, startDate: e.target.value }); }} />
                                                    <span style={{ display: 'inline-block', margin: '0 5px' }}>~</span>
                                                    <input type="date" style={{ ...inp, display: 'inline-block', width: '45%' }} value={newEducation.endDate || ''} onChange={(e) => { if (newEducation.startDate && e.target.value < newEducation.startDate) { toast.error('종료일은 시작일보다 빠를 수 없습니다.', toastConfig); return; } setNewEducation({ ...newEducation, endDate: e.target.value }); }} />
                                                </td>
                                                <td style={edTdS}><input type="text" style={inp} value={newEducation.major || ''} onChange={(e) => setNewEducation({ ...newEducation, major: e.target.value })} /></td>
                                                <td style={edTdS}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 38 }}>
                                                        {newEducation.file ? <span style={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.85rem' }} title={newEducation.file.name}>{newEducation.file.name}</span> : <span style={{ color: '#9ca3af', fontSize: 12, flexShrink: 0 }}>선택 없음</span>}
                                                        <label htmlFor="new-edu-file" style={{ ...b('outline-secondary', true), marginBottom: 0, flexShrink: 0, cursor: 'pointer' }}>{newEducation.file ? "교체" : "파일 선택"}</label>
                                                        <input id="new-edu-file" type="file" hidden onChange={(e) => setNewEducation({ ...newEducation, file: e.target.files[0] })} />
                                                    </div>
                                                </td>
                                                <td style={edTdS}>
                                                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                                        <button style={b('primary', true)} onClick={handleCreateEducation}><FaCheck /></button>
                                                        <button style={b('danger', true)} onClick={cancelCreateEducation}><MdCancel /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        {personalInfo.educationList.length > 0 ? (
                                            personalInfo.educationList.map((education, index) => (
                                                isEducationEditing && targetEducationEditing === education.id ? (
                                                    <tr key={index}>
                                                        <td style={edTdS}><input type="text" style={inp} value={editEducation.schoolName || ''} onChange={(e) => setEditEducation({ ...editEducation, schoolName: e.target.value })} /></td>
                                                        <td style={edTdS}><Select isSearchable={false} styles={selS} defaultValue={degreeOptions[0]} options={degreeOptions} onChange={(e) => setEditEducation({ ...editEducation, degree: e.value })} /></td>
                                                        <td style={{ ...edTdS, textAlign: 'center' }}>
                                                            <input type="date" style={{ ...inp, display: 'inline-block', width: '45%' }} value={editEducation.startDate || ''} onChange={(e) => { if (editEducation.endDate && e.target.value > editEducation.endDate) { toast.error('시작일은 종료일보다 늦을 수 없습니다.', toastConfig); return; } setEditEducation({ ...editEducation, startDate: e.target.value }); }} />
                                                            <span style={{ display: 'inline-block', margin: '0 5px' }}>~</span>
                                                            <input type="date" style={{ ...inp, display: 'inline-block', width: '45%' }} value={editEducation.endDate || ''} onChange={(e) => { if (editEducation.startDate && e.target.value < editEducation.startDate) { toast.error('종료일은 시작일보다 빠를 수 없습니다.', toastConfig); return; } setEditEducation({ ...editEducation, endDate: e.target.value }); }} />
                                                        </td>
                                                        <td style={edTdS}><input type="text" style={inp} value={editEducation.major || ''} onChange={(e) => setEditEducation({ ...editEducation, major: e.target.value })} /></td>
                                                        <td style={edTdS}>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                                                {education.fileName && <span style={{ maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{education.fileName}</span>}
                                                                <label htmlFor={`edu-file-edit-${education.id}`} style={{ ...b('outline-primary', true), marginBottom: 0, cursor: 'pointer' }}>{education.fileName ? '교체' : '등록'}</label>
                                                                <input id={`edu-file-edit-${education.id}`} type="file" hidden onChange={(e) => handleFileUpload('education', education.id, e.target.files[0])} />
                                                            </div>
                                                        </td>
                                                        <td style={edTdS}>
                                                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                                                <button style={b('primary', true)} onClick={handleUpdateEducation}><FaCheck /></button>
                                                                <button style={b('danger', true)} onClick={cancelModifyEducation}><MdCancel /></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    <tr key={index}>
                                                        <td style={{ ...tdS, fontWeight: 600, color: '#1f1d2e' }}>{education.schoolName}</td>
                                                        <td style={tdS}>{education.degree || '-'}</td>
                                                        <td style={tdS}>{education.startDate} ~ {education.endDate}</td>
                                                        <td style={tdS}>{education.major || "-"}</td>
                                                        <td style={{ ...tdS, textAlign: 'center' }}>
                                                            {education.fileName ? (
                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                                                    <span style={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={education.fileName}>{education.fileName}</span>
                                                                    <button style={b('outline-success', true)} onClick={() => handleFileDownload('education', education.id, education.fileName)}>다운</button>
                                                                    <label htmlFor={`edu-file-${education.id}`} style={{ ...b('outline-secondary', true), marginBottom: 0, cursor: 'pointer' }}>교체</label>
                                                                </div>
                                                            ) : (
                                                                <label htmlFor={`edu-file-${education.id}`} style={{ ...b('outline-primary', true), marginBottom: 0, cursor: 'pointer' }}>파일 선택</label>
                                                            )}
                                                            <input id={`edu-file-${education.id}`} type="file" hidden onChange={(e) => handleFileUpload('education', education.id, e.target.files[0])} />
                                                        </td>
                                                        <td style={{ ...tdS, textAlign: 'center' }}>
                                                            {rowAct(() => modifyEducation(education.id, education), () => deleteEducation(education.schoolName, education.id))}
                                                        </td>
                                                    </tr>
                                                )
                                            ))
                                        ) : emptyRow(6, '학력 정보가 없습니다.')}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* IT 교육 */}
                        <div style={cardS}>
                            {sHead('IT 교육', personalInfo.itTrainingList?.length ?? 0, addBtn('추가', () => setIsItTrainingAdding(true)), (personalInfo.itTrainingList?.length ?? 0) === 0 ? '#aaa9b7' : undefined)}
                            <div style={{ overflowX: 'auto' }}>
                                <table style={tblS}>
                                    <colgroup>
                                        <col width={'20%'} /><col width={'18%'} /><col width={'22%'} /><col width={'*'} /><col width={'7%'} />
                                    </colgroup>
                                    <thead>
                                        <tr style={{ background: '#f7f6fb' }}>
                                            <th style={{ ...thS, textAlign: 'left' }}>교육명</th>
                                            <th style={{ ...thS, textAlign: 'left' }}>교육기관</th>
                                            <th style={thS}>기간</th>
                                            <th style={{ ...thS, textAlign: 'left' }}>교육내용</th>
                                            <th style={thS}>관리</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isItTrainingAdding && (
                                            <tr>
                                                <td style={edTdS}><input type="text" style={inp} value={newItTraining.title} onChange={(e) => setNewItTraining({ ...newItTraining, title: e.target.value })} /></td>
                                                <td style={edTdS}><input type="text" style={inp} value={newItTraining.institution} onChange={(e) => setNewItTraining({ ...newItTraining, institution: e.target.value })} /></td>
                                                <td style={{ ...edTdS, textAlign: 'center' }}>
                                                    <input type="date" style={{ ...inp, display: 'inline-block', width: '45%' }} value={newItTraining.startDate || ''} onChange={(e) => { if (newItTraining.endDate && e.target.value > newItTraining.endDate) { toast.error('시작일은 종료일보다 늦을 수 없습니다.', toastConfig); return; } setNewItTraining({ ...newItTraining, startDate: e.target.value }); }} />
                                                    <span style={{ display: 'inline-block', margin: '0 5px' }}>~</span>
                                                    <input type="date" style={{ ...inp, display: 'inline-block', width: '45%' }} value={newItTraining.endDate || ''} onChange={(e) => { if (newItTraining.startDate && e.target.value < newItTraining.startDate) { toast.error('종료일은 시작일보다 빠를 수 없습니다.', toastConfig); return; } setNewItTraining({ ...newItTraining, endDate: e.target.value }); }} />
                                                </td>
                                                <td style={edTdS}><input type="text" style={inp} value={newItTraining.description} onChange={(e) => setNewItTraining({ ...newItTraining, description: e.target.value })} /></td>
                                                <td style={edTdS}>
                                                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                                        <button style={b('primary', true)} onClick={handleCreateItTraining}><FaCheck /></button>
                                                        <button style={b('danger', true)} onClick={cancelCreateItTraining}><MdCancel /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        {personalInfo.itTrainingList && personalInfo.itTrainingList.length > 0 ? (
                                            personalInfo.itTrainingList.map((training, index) => (
                                                isItTrainingEditing && targetItTrainingEditing === training.id ? (
                                                    <tr key={index}>
                                                        <td style={edTdS}><input type="text" style={inp} value={editItTraining.title || ''} onChange={(e) => setEditItTraining({ ...editItTraining, title: e.target.value })} /></td>
                                                        <td style={edTdS}><input type="text" style={inp} value={editItTraining.institution || ''} onChange={(e) => setEditItTraining({ ...editItTraining, institution: e.target.value })} /></td>
                                                        <td style={{ ...edTdS, textAlign: 'center' }}>
                                                            <input type="date" style={{ ...inp, display: 'inline-block', width: '45%' }} value={editItTraining.startDate || ''} onChange={(e) => { if (editItTraining.endDate && e.target.value > editItTraining.endDate) { toast.error('시작일은 종료일보다 늦을 수 없습니다.', toastConfig); return; } setEditItTraining({ ...editItTraining, startDate: e.target.value }); }} />
                                                            <span style={{ display: 'inline-block', margin: '0 5px' }}>~</span>
                                                            <input type="date" style={{ ...inp, display: 'inline-block', width: '45%' }} value={editItTraining.endDate || ''} onChange={(e) => { if (editItTraining.startDate && e.target.value < editItTraining.startDate) { toast.error('종료일은 시작일보다 빠를 수 없습니다.', toastConfig); return; } setEditItTraining({ ...editItTraining, endDate: e.target.value }); }} />
                                                        </td>
                                                        <td style={edTdS}><input type="text" style={inp} value={editItTraining.description || ''} onChange={(e) => setEditItTraining({ ...editItTraining, description: e.target.value })} /></td>
                                                        <td style={edTdS}>
                                                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                                                <button style={b('primary', true)} onClick={handleUpdateItTraining}><FaCheck /></button>
                                                                <button style={b('danger', true)} onClick={cancelModifyItTraining}><MdCancel /></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    <tr key={index}>
                                                        <td style={tdS}>{training.title}</td>
                                                        <td style={tdS}>{training.institution}</td>
                                                        <td style={tdS}>{training.startDate} ~ {training.endDate || '진행 중'}</td>
                                                        <td style={tdS}>{training.description || '-'}</td>
                                                        <td style={{ ...tdS, textAlign: 'center' }}>
                                                            {rowAct(() => modifyItTraining(training.id, training), () => deleteItTraining(training.title, training.id))}
                                                        </td>
                                                    </tr>
                                                )
                                            ))
                                        ) : emptyRow(5, 'IT 교육 정보가 없습니다.')}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* 특수기술 */}
                        <div style={cardS}>
                            {sHead('특수기술', personalInfo.specialSkillList?.length ?? 0, addBtn('추가', () => setIsSpecialSkillAdding(true)), (personalInfo.specialSkillList?.length ?? 0) === 0 ? '#aaa9b7' : undefined)}
                            <div style={{ overflowX: 'auto' }}>
                                <table style={tblS}>
                                    <colgroup>
                                        <col width={'*'} /><col width={'25%'} /><col width={'7%'} />
                                    </colgroup>
                                    <thead>
                                        <tr style={{ background: '#f7f6fb' }}>
                                            <th style={{ ...thS, textAlign: 'left' }}>특수기술명</th>
                                            <th style={{ ...thS, textAlign: 'left' }}>숙련도</th>
                                            <th style={thS}>관리</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isSpecialSkillAdding && (
                                            <tr>
                                                <td style={edTdS}><input type="text" style={inp} value={newSpecialSkill.skillName} onChange={(e) => setNewSpecialSkill({ ...newSpecialSkill, skillName: e.target.value })} placeholder="특수기술명" /></td>
                                                <td style={edTdS}><input type="text" style={inp} value={newSpecialSkill.proficiency} onChange={(e) => setNewSpecialSkill({ ...newSpecialSkill, proficiency: e.target.value })} placeholder="숙련도" /></td>
                                                <td style={edTdS}>
                                                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                                        <button style={b('primary', true)} onClick={handleCreateSpecialSkill}><FaCheck /></button>
                                                        <button style={b('danger', true)} onClick={cancelCreateSpecialSkill}><MdCancel /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        {personalInfo.specialSkillList && personalInfo.specialSkillList.length > 0 ? (
                                            personalInfo.specialSkillList.map((skill, index) => (
                                                isSpecialSkillEditing && targetSpecialSkillEditing === skill.id ? (
                                                    <tr key={index}>
                                                        <td style={edTdS}><input type="text" style={inp} value={editSpecialSkill.skillName || ''} onChange={(e) => setEditSpecialSkill({ ...editSpecialSkill, skillName: e.target.value })} /></td>
                                                        <td style={edTdS}><input type="text" style={inp} value={editSpecialSkill.proficiency || ''} onChange={(e) => setEditSpecialSkill({ ...editSpecialSkill, proficiency: e.target.value })} /></td>
                                                        <td style={edTdS}>
                                                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                                                <button style={b('primary', true)} onClick={handleUpdateSpecialSkill}><FaCheck /></button>
                                                                <button style={b('danger', true)} onClick={cancelModifySpecialSkill}><MdCancel /></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    <tr key={index}>
                                                        <td style={tdS}>{skill.skillName || '-'}</td>
                                                        <td style={tdS}>{skill.proficiency || '-'}</td>
                                                        <td style={{ ...tdS, textAlign: 'center' }}>
                                                            {rowAct(() => modifySpecialSkill(skill.id, skill), () => deleteSpecialSkill(skill.skillName, skill.id))}
                                                        </td>
                                                    </tr>
                                                )
                                            ))
                                        ) : emptyRow(3, '특수기술 정보가 없습니다.')}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* 자격증 */}
                        <div style={cardS}>
                            {sHead('자격증', personalInfo.certificateList.length, addBtn('추가', () => setIsCertificateAdding(true)))}
                            <div style={{ overflowX: 'auto' }}>
                                <table style={tblS}>
                                    <colgroup>
                                        <col width={'24%'} /><col width={'13%'} /><col width={'*'} /><col width={'9%'} /><col width={'7%'} />
                                    </colgroup>
                                    <thead>
                                        <tr style={{ background: '#f7f6fb' }}>
                                            <th style={{ ...thS, textAlign: 'left' }}>자격증 및 언어</th>
                                            <th style={thS}>취득일</th>
                                            <th style={thS}>증빙서류</th>
                                            <th style={thS}>경력프로필</th>
                                            <th style={thS}>관리</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isCertificateAdding && (
                                            <tr>
                                                <td style={edTdS}>
                                                    <CreatableSelect isClearable styles={selS} placeholder="자격증 이름을 입력하세요." onChange={(e) => setNewCertificate({ ...newCertificate, certificateName: e === null ? '' : e.value })} options={certificateDataList} />
                                                </td>
                                                <td style={edTdS}><input type="date" style={inp} value={newCertificate.acquisitionDate || ''} onChange={(e) => setNewCertificate({ ...newCertificate, acquisitionDate: e.target.value })} /></td>
                                                <td style={edTdS}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 38 }}>
                                                        {newCertificate.file ? <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.85rem' }} title={newCertificate.file.name}>{newCertificate.file.name}</span> : <span style={{ color: '#9ca3af', fontSize: 12, flexShrink: 0 }}>선택 없음</span>}
                                                        <label htmlFor="new-cert-file" style={{ ...b('outline-secondary', true), marginBottom: 0, flexShrink: 0, cursor: 'pointer' }}>{newCertificate.file ? "교체" : "파일 선택"}</label>
                                                        <input id="new-cert-file" type="file" hidden onChange={(e) => setNewCertificate({ ...newCertificate, file: e.target.files[0] })} />
                                                    </div>
                                                </td>
                                                <td style={{ ...edTdS, textAlign: 'center' }}>
                                                    <input type="checkbox" style={{ accentColor: PURPLE, cursor: 'pointer' }} checked={newCertificate.includeInCareerProfile !== false} onChange={(e) => setNewCertificate({ ...newCertificate, includeInCareerProfile: e.target.checked })} />
                                                </td>
                                                <td style={edTdS}>
                                                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                                        <button style={b('primary', true)} onClick={handleCreateCertificate}><FaCheck /></button>
                                                        <button style={b('danger', true)} onClick={cancelCreateCertificate}><MdCancel /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        {personalInfo.certificateList.length > 0 ? (
                                            personalInfo.certificateList.map((certificate, index) => (
                                                isCertificateEditing && certificate.id === targetCertificateEditing ? (
                                                    <tr key={index}>
                                                        <td style={edTdS}>
                                                            <CreatableSelect isClearable styles={selS} placeholder="자격증 이름을 입력하세요." defaultValue={certificateDataList.filter((option) => option.value === editCertificate.certificateName)} onChange={(e) => setEditCertificate({ ...editCertificate, certificateName: e === null ? '' : e.value })} options={certificateDataList} />
                                                        </td>
                                                        <td style={edTdS}>
                                                            <input type="date" style={inp} value={editCertificate.acquisitionDate || ''} onChange={(e) => setEditCertificate({ ...editCertificate, acquisitionDate: e.target.value })} />
                                                        </td>
                                                        <td style={edTdS}>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                                                {certificate.fileName && <span style={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{certificate.fileName}</span>}
                                                                <label htmlFor={`cert-file-edit-${certificate.id}`} style={{ ...b('outline-primary', true), marginBottom: 0, cursor: 'pointer' }}>{certificate.fileName ? '교체' : '등록'}</label>
                                                                <input id={`cert-file-edit-${certificate.id}`} type="file" hidden onChange={(e) => handleFileUpload('certificate', certificate.id, e.target.files[0])} />
                                                            </div>
                                                        </td>
                                                        <td style={{ ...edTdS, textAlign: 'center' }}>
                                                            <input type="checkbox" style={{ accentColor: PURPLE, cursor: 'pointer' }} checked={editCertificate.includeInCareerProfile !== false} onChange={(e) => setEditCertificate({ ...editCertificate, includeInCareerProfile: e.target.checked })} />
                                                        </td>
                                                        <td style={edTdS}>
                                                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                                                <button style={b('primary', true)} onClick={handleUpdateCertificate}><FaCheck /></button>
                                                                <button style={b('danger', true)} onClick={cancelModifyCertificate}><MdCancel /></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    <tr key={index}>
                                                        <td style={{ ...tdS, fontWeight: 600, color: '#1f1d2e' }}>{certificate.certificateName}</td>
                                                        <td style={tdS}>{certificate.acquisitionDate}</td>
                                                        <td style={{ ...tdS, textAlign: 'center' }}>
                                                            {certificate.certificateName !== '운전면허증' ? (
                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                                                    {certificate.fileName ? (
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                            <span style={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={certificate.fileName}>{certificate.fileName}</span>
                                                                            <button style={b('outline-success', true)} onClick={() => handleFileDownload('certificate', certificate.id, certificate.fileName)}>다운</button>
                                                                            <label htmlFor={`cert-file-${certificate.id}`} style={{ ...b('outline-secondary', true), marginBottom: 0, cursor: 'pointer' }}>교체</label>
                                                                        </div>
                                                                    ) : (
                                                                        <label htmlFor={`cert-file-${certificate.id}`} style={{ ...b('outline-primary', true), marginBottom: 0, cursor: 'pointer' }}>파일 선택</label>
                                                                    )}
                                                                    <input id={`cert-file-${certificate.id}`} type="file" hidden onChange={(e) => handleFileUpload('certificate', certificate.id, e.target.files[0])} />
                                                                </div>
                                                            ) : (
                                                                <span style={{ color: '#9ca3af', fontSize: 12 }}>업로드 대상 아님</span>
                                                            )}
                                                        </td>
                                                        <td style={{ ...tdS, textAlign: 'center' }}>
                                                            <input type="checkbox" style={{ accentColor: PURPLE, cursor: 'pointer' }} checked={certificate.includeInCareerProfile !== false} onChange={() => handleToggleCareerProfile(certificate)} title="경력프로필에 표시" />
                                                        </td>
                                                        <td style={{ ...tdS, textAlign: 'center' }}>
                                                            {rowAct(() => modifyCertificate(certificate.id, certificate), () => deleteCertificate(certificate.certificateName, certificate.id))}
                                                        </td>
                                                    </tr>
                                                )
                                            ))
                                        ) : emptyRow(5, '자격증 정보가 없습니다.')}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* 현재 조직 프로젝트 참여 이력 */}
                        <div style={cardS}>
                            {sHead('현재 조직 프로젝트 참여 이력', personalInfo.projectList?.length, undefined, MINT)}
                            <div style={{ overflowX: 'auto' }}>
                                <table style={tblS}>
                                    <colgroup>
                                        <col width={'14%'} /><col width={'10%'} /><col width={'16%'} /><col width={'7%'} /><col width={'9%'} /><col width={'*'} /><col width={'20%'} />
                                    </colgroup>
                                    <thead>
                                        <tr style={{ background: '#f7f6fb' }}>
                                            <th style={{ ...thS, textAlign: 'left' }}>프로젝트명</th>
                                            <th style={{ ...thS, textAlign: 'left' }}>고객사</th>
                                            <th style={thS}>참여 기간</th>
                                            <th style={thS}>등급</th>
                                            <th style={thS}>역할</th>
                                            <th style={{ ...thS, textAlign: 'left' }}>담당 업무</th>
                                            <th style={{ ...thS, textAlign: 'left' }}>기술스택</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {personalInfo.projectList && personalInfo.projectList.length > 0 ? (
                                            personalInfo.projectList.map((project, index) => (
                                                <tr key={index} style={{ transition: 'background 100ms' }} onMouseEnter={(e) => e.currentTarget.style.background = '#fafaff'} onMouseLeave={(e) => e.currentTarget.style.background = ''}>
                                                    <td style={{ ...tdS, fontWeight: 600, color: '#1f1d2e' }}>{project.projectName || "-"}</td>
                                                    <td style={tdS}>{project.client || "-"}</td>
                                                    <td style={{ ...tdS, whiteSpace: 'nowrap', color: '#5d5b6e', fontVariantNumeric: 'tabular-nums' }}>
                                                        {project.startDate} ~ {project.endDate || "진행 중"}
                                                        {project.careerDisplay && <div style={{ color: '#9ca3af', fontSize: 11 }}>({project.careerDisplay})</div>}
                                                    </td>
                                                    <td style={{ ...tdS, textAlign: 'center' }}>{gradeBadge(project.skillLevel)}</td>
                                                    <td style={{ ...tdS, textAlign: 'center' }}>{project.role || "-"}</td>
                                                    <td style={tdS}>{project.assignments || "-"}</td>
                                                    <td style={{ ...tdS, fontSize: '0.85rem' }}>{[project.os, project.dbInfo, project.language].filter(Boolean).join(' / ') || "-"}</td>
                                                </tr>
                                            ))
                                        ) : emptyRow(7, '참여 중인 프로젝트 정보가 없습니다.')}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* 경력 */}
                        <div style={cardS}>
                            {sHead('경력', personalInfo.careerList.length, addBtn('추가', () => setIsCareerAdding(true)))}
                            <div style={{ overflowX: 'auto' }}>
                                <table style={tblS}>
                                    <colgroup>
                                        <col width={'22%'} /><col width={'25%'} /><col width={'13%'} /><col width={'33%'} /><col width={'7%'} />
                                    </colgroup>
                                    <thead>
                                        <tr style={{ background: '#f7f6fb' }}>
                                            <th style={{ ...thS, textAlign: 'left' }}>근무처</th>
                                            <th style={thS}>기간</th>
                                            <th style={thS}>직위</th>
                                            <th style={{ ...thS, textAlign: 'left' }}>업무내용</th>
                                            <th style={thS}>관리</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isCareerAdding && (
                                            <tr>
                                                <td style={edTdS}><input type="text" style={inp} value={newCareer.organizationName || ''} onChange={(e) => setNewCareer({ ...newCareer, organizationName: e.target.value })} /></td>
                                                <td style={{ ...edTdS, textAlign: 'center' }}>
                                                    <input type="date" style={{ ...inp, display: 'inline-block', width: '45%' }} value={newCareer.startDate || ''} onChange={(e) => { if (newCareer.endDate && e.target.value > newCareer.endDate) { toast.error('시작일은 종료일보다 늦을 수 없습니다.', toastConfig); return; } setNewCareer({ ...newCareer, startDate: e.target.value }); }} />
                                                    <span style={{ display: 'inline-block', margin: '0 5px' }}>~</span>
                                                    <input type="date" style={{ ...inp, display: 'inline-block', width: '45%' }} value={newCareer.endDate || ''} onChange={(e) => { if (newCareer.startDate && e.target.value < newCareer.startDate) { toast.error('종료일은 시작일보다 빠를 수 없습니다.', toastConfig); return; } setNewCareer({ ...newCareer, endDate: e.target.value }); }} />
                                                </td>
                                                <td style={edTdS}><input type="text" style={inp} value={newCareer.position || ''} onChange={(e) => setNewCareer({ ...newCareer, position: e.target.value })} /></td>
                                                <td style={edTdS}><input type="text" style={inp} value={newCareer.task || ''} onChange={(e) => setNewCareer({ ...newCareer, task: e.target.value })} /></td>
                                                <td style={edTdS}>
                                                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                                        <button style={b('primary', true)} onClick={handleCreateCareer}><FaCheck /></button>
                                                        <button style={b('danger', true)} onClick={cancelCreateCareer}><MdCancel /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        {personalInfo.careerList.length > 0 ? (
                                            personalInfo.careerList.map((career, index) => (
                                                isCareerEditing && targetCareerEditing === career.careerId ? (
                                                    <tr key={index}>
                                                        <td style={edTdS}><input type="text" style={inp} value={editCareer.organizationName || ''} onChange={(e) => setEditCareer({ ...editCareer, organizationName: e.target.value })} /></td>
                                                        <td style={{ ...edTdS, textAlign: 'center' }}>
                                                            <input type="date" style={{ ...inp, display: 'inline-block', width: '45%' }} value={editCareer.startDate || ''} onChange={(e) => { if (editCareer.endDate && e.target.value > editCareer.endDate) { toast.error('시작일은 종료일보다 늦을 수 없습니다.', toastConfig); return; } setEditCareer({ ...editCareer, startDate: e.target.value }); }} />
                                                            <span style={{ display: 'inline-block', margin: '0 5px' }}>~</span>
                                                            <input type="date" style={{ ...inp, display: 'inline-block', width: '45%' }} value={editCareer.endDate || ''} onChange={(e) => { if (editCareer.startDate && e.target.value < editCareer.startDate) { toast.error('종료일은 시작일보다 빠를 수 없습니다.', toastConfig); return; } setEditCareer({ ...editCareer, endDate: e.target.value }); }} />
                                                        </td>
                                                        <td style={edTdS}><input type="text" style={inp} value={editCareer.position || ''} onChange={(e) => setEditCareer({ ...editCareer, position: e.target.value })} /></td>
                                                        <td style={edTdS}><input type="text" style={inp} value={editCareer.task || ''} onChange={(e) => setEditCareer({ ...editCareer, task: e.target.value })} /></td>
                                                        <td style={edTdS}>
                                                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                                                <button style={b('primary', true)} onClick={handleUpdateCareer}><FaCheck /></button>
                                                                <button style={b('danger', true)} onClick={cancelModifyCareer}><MdCancel /></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    <tr key={index}>
                                                        <td style={{ ...tdS, fontWeight: 600, color: '#1f1d2e' }}>{career.organizationName}</td>
                                                        <td style={{ ...tdS, whiteSpace: 'nowrap', color: '#5d5b6e', fontVariantNumeric: 'tabular-nums' }}>{career.startDate} ~ {career.endDate || "재직중"}</td>
                                                        <td style={{ ...tdS, textAlign: 'center' }}>{career.position}</td>
                                                        <td style={tdS}>{career.task}</td>
                                                        <td style={{ ...tdS, textAlign: 'center' }}>
                                                            {rowAct(() => modifyCareer(career.careerId, career), () => deleteCareer(career.organizationName, career.careerId))}
                                                        </td>
                                                    </tr>
                                                )
                                            ))
                                        ) : emptyRow(5, '경력 정보가 없습니다.')}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* 이전 프로젝트 */}
                        <div style={cardS}>
                            {sHead('이전 프로젝트', personalInfo.preProjectList.length, addBtn('추가', () => setIsPreProjectAdding(true)))}
                            <div style={{ overflowX: 'auto' }}>
                                <table style={tblS}>
                                    <colgroup>
                                        <col width={'12%'} /><col width={'18%'} /><col width={'8%'} /><col width={'8%'} /><col width={'7%'} /><col width={'*'} /><col width={'16%'} /><col width={'7%'} />
                                    </colgroup>
                                    <thead>
                                        <tr style={{ background: '#f7f6fb' }}>
                                            <th style={{ ...thS, textAlign: 'left' }}>프로젝트이름</th>
                                            <th style={thS}>기간</th>
                                            <th style={thS}>고객이름</th>
                                            <th style={{ ...thS, textAlign: 'left' }}>회사이름</th>
                                            <th style={thS}>역할</th>
                                            <th style={{ ...thS, textAlign: 'left' }}>업무내용</th>
                                            <th style={{ ...thS, textAlign: 'left' }}>기술스택</th>
                                            <th style={thS}>관리</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isPreProjectAdding && (
                                            <tr>
                                                <td style={edTdS}><input type="text" style={inp} value={newPreProject.projectName || ''} onChange={(e) => setNewPreProject({ ...newPreProject, projectName: e.target.value })} /></td>
                                                <td style={{ ...edTdS, textAlign: 'center' }}>
                                                    <input type="date" style={{ ...inp, display: 'inline-block', width: '45%' }} value={newPreProject.startDate || ''} onChange={(e) => { if (newPreProject.endDate && e.target.value > newPreProject.endDate) { toast.error('시작일은 종료일보다 늦을 수 없습니다.', toastConfig); return; } setNewPreProject({ ...newPreProject, startDate: e.target.value }); }} />
                                                    <span style={{ display: 'inline-block', margin: '0 5px' }}>~</span>
                                                    <input type="date" style={{ ...inp, display: 'inline-block', width: '45%' }} value={newPreProject.endDate || ''} onChange={(e) => { if (newPreProject.startDate && e.target.value < newPreProject.startDate) { toast.error('종료일은 시작일보다 빠를 수 없습니다.', toastConfig); return; } setNewPreProject({ ...newPreProject, endDate: e.target.value }); }} />
                                                </td>
                                                <td style={edTdS}><input type="text" style={inp} value={newPreProject.client || ''} onChange={(e) => setNewPreProject({ ...newPreProject, client: e.target.value })} /></td>
                                                <td style={edTdS}><input type="text" style={inp} value={newPreProject.companyName || ''} onChange={(e) => setNewPreProject({ ...newPreProject, companyName: e.target.value })} /></td>
                                                <td style={edTdS}><input type="text" style={inp} value={newPreProject.role || ''} onChange={(e) => setNewPreProject({ ...newPreProject, role: e.target.value })} /></td>
                                                <td style={edTdS}><input type="text" style={inp} value={newPreProject.content || ''} onChange={(e) => setNewPreProject({ ...newPreProject, content: e.target.value })} /></td>
                                                <td style={edTdS}>
                                                    <button style={{ ...b('outline-secondary', true), width: '100%', marginBottom: 4 }} onClick={() => handleOpenPreProjectTech('new')}>기술스택 설정</button>
                                                    {(newPreProject.os || newPreProject.dbInfo || newPreProject.language) && (
                                                        <small style={{ color: '#9ca3af', fontSize: '0.7rem', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={[newPreProject.os, newPreProject.dbInfo, newPreProject.language].filter(Boolean).join(' / ')}>
                                                            {[newPreProject.os, newPreProject.dbInfo, newPreProject.language].filter(Boolean).join(' / ')}
                                                        </small>
                                                    )}
                                                </td>
                                                <td style={edTdS}>
                                                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                                        <button style={b('primary', true)} onClick={handleCreatePreProject}><FaCheck /></button>
                                                        <button style={b('danger', true)} onClick={cancelCreatePreProject}><MdCancel /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        {personalInfo.preProjectList.length > 0 ? (
                                            personalInfo.preProjectList.map((project, index) => (
                                                isPreProjectEditing && targetPreProjectEditing === project.id ? (
                                                    <tr key={index}>
                                                        <td style={edTdS}><input type="text" style={inp} value={editPreProject.projectName || ''} onChange={(e) => setEditPreProject({ ...editPreProject, projectName: e.target.value })} /></td>
                                                        <td style={{ ...edTdS, textAlign: 'center' }}>
                                                            <input type="date" style={{ ...inp, display: 'inline-block', width: '45%' }} value={editPreProject.startDate || ''} onChange={(e) => { if (editPreProject.endDate && e.target.value > editPreProject.endDate) { toast.error('시작일은 종료일보다 늦을 수 없습니다.', toastConfig); return; } setEditPreProject({ ...editPreProject, startDate: e.target.value }); }} />
                                                            <span style={{ display: 'inline-block', margin: '0 5px' }}>~</span>
                                                            <input type="date" style={{ ...inp, display: 'inline-block', width: '45%' }} value={editPreProject.endDate || ''} onChange={(e) => { if (editPreProject.startDate && e.target.value < editPreProject.startDate) { toast.error('종료일은 시작일보다 빠를 수 없습니다.', toastConfig); return; } setEditPreProject({ ...editPreProject, endDate: e.target.value }); }} />
                                                        </td>
                                                        <td style={edTdS}><input type="text" style={inp} value={editPreProject.client || ''} onChange={(e) => setEditPreProject({ ...editPreProject, client: e.target.value })} /></td>
                                                        <td style={edTdS}><input type="text" style={inp} value={editPreProject.companyName || ''} onChange={(e) => setEditPreProject({ ...editPreProject, companyName: e.target.value })} /></td>
                                                        <td style={edTdS}><input type="text" style={inp} value={editPreProject.role || ''} onChange={(e) => setEditPreProject({ ...editPreProject, role: e.target.value })} /></td>
                                                        <td style={edTdS}><input type="text" style={inp} value={editPreProject.content || ''} onChange={(e) => setEditPreProject({ ...editPreProject, content: e.target.value })} /></td>
                                                        <td style={edTdS}>
                                                            <button style={{ ...b('outline-secondary', true), width: '100%', marginBottom: 4 }} onClick={() => handleOpenPreProjectTech('edit')}>기술스택 설정</button>
                                                            {(editPreProject.os || editPreProject.dbInfo || editPreProject.language) && (
                                                                <small style={{ color: '#9ca3af', fontSize: '0.7rem', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={[editPreProject.os, editPreProject.dbInfo, editPreProject.language].filter(Boolean).join(' / ')}>
                                                                    {[editPreProject.os, editPreProject.dbInfo, editPreProject.language].filter(Boolean).join(' / ')}
                                                                </small>
                                                            )}
                                                        </td>
                                                        <td style={edTdS}>
                                                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                                                <button style={b('primary', true)} onClick={handleUpdatePreProject}><FaCheck /></button>
                                                                <button style={b('danger', true)} onClick={cancelModifyPreProject}><MdCancel /></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    <tr key={index}>
                                                        <td style={{ ...tdS, fontWeight: 600, color: '#1f1d2e' }}>{project.projectName}</td>
                                                        <td style={{ ...tdS, whiteSpace: 'nowrap', color: '#5d5b6e', fontVariantNumeric: 'tabular-nums' }}>{project.startDate} ~ {project.endDate}</td>
                                                        <td style={{ ...tdS, textAlign: 'center' }}>{project.client}</td>
                                                        <td style={tdS}>{project.companyName}</td>
                                                        <td style={{ ...tdS, textAlign: 'center' }}>{project.role || '-'}</td>
                                                        <td style={tdS}>{project.content}</td>
                                                        <td style={{ ...tdS, fontSize: '0.85rem' }}>{[project.os, project.dbInfo, project.language].filter(Boolean).join(' / ') || '-'}</td>
                                                        <td style={{ ...tdS, textAlign: 'center' }}>
                                                            {rowAct(() => modifyPreProject(project.id, project), () => deletePreProject(project.projectName, project.id))}
                                                        </td>
                                                    </tr>
                                                )
                                            ))
                                        ) : emptyRow(8, '이전 프로젝트 정보가 없습니다.')}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            {/* 기술스택 설정 모달 */}
            {showPreProjectTechModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: 'white', borderRadius: 16, padding: '24px', width: 480, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(60,50,120,.2)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <h5 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1f1d2e' }}>기술스택 설정</h5>
                            <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#6b7280', lineHeight: 1 }} onClick={() => setShowPreProjectTechModal(false)}>×</button>
                        </div>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>OS</label>
                            <input type="text" style={inp} value={preProjectTechForm.os} onChange={(e) => setPreProjectTechForm(prev => ({ ...prev, os: e.target.value }))} placeholder="예: Windows, Linux, macOS" />
                        </div>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>DB</label>
                            <input type="text" style={inp} value={preProjectTechForm.dbInfo} onChange={(e) => setPreProjectTechForm(prev => ({ ...prev, dbInfo: e.target.value }))} placeholder="예: Oracle, MySQL, PostgreSQL" />
                        </div>
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>언어</label>
                            <input type="text" style={inp} value={preProjectTechForm.language} onChange={(e) => setPreProjectTechForm(prev => ({ ...prev, language: e.target.value }))} placeholder="예: Java, Python, React" />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button style={b('outline-secondary')} onClick={() => setShowPreProjectTechModal(false)}>취소</button>
                            <button style={b('primary')} onClick={handleSavePreProjectTech}>저장</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MyInfoDetails;
